import React, { useState, useEffect } from 'react';
import './addOrRemoveUser.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUserPlus, faUserMinus, faShieldAlt } from '@fortawesome/free-solid-svg-icons';
import axios from 'axios';
import UserResponsePopup from '../shared/Popup/UserPopup';
import { useAuth } from '../../context/AuthContext';

const AddOrRemoveUser: React.FC = () => {
    const { isLoggedIn } = useAuth();
    const [userName, setUserName] = useState('');
    const [errorMsg, setErrorMsg] = useState<string>('');
    const [successMsg, setSuccessMsg] = useState<string>('');
    const [isResponsePopupOpen, setIsResponsePopupOpen] = useState(false);
    const [isSuccess, setIsSuccess] = useState(false);
    const [responseMessage, setResponseMessage] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [users, setUsers] = useState<{ id: number; name: string }[]>([]);

    const [newAdminUsername, setNewAdminUsername] = useState('');
    const [newAdminPassword, setNewAdminPassword] = useState('');
    const [adminUsers, setAdminUsers] = useState<{ id: number; username: string; created_at: string }[]>([]);
    const [isAdminLoading, setIsAdminLoading] = useState(false);

    useEffect(() => {
        getAllUsers();
        if (isLoggedIn) {
            getAdminUsers();
        }
    }, [isLoggedIn])

    useEffect(() => {
        setErrorMsg('');
        setSuccessMsg('');
    }, [userName]);

    const getAllUsers = async () => {
        try {
            setIsLoading(true);
            const response = await axios.get('https://kaskas.pro/split-bill-server/users');
            console.log(response.data);
            setUsers(response.data);
        } catch (error) {
            console.log(error);
        } finally {
            setIsLoading(false);
        }

    }

    const handleAddUser = async () => {
        if (!isUsernameValid()) {
            return;
        }
        setIsLoading(true);
        try {
            const response = await axios.post('https://kaskas.pro/split-bill-server/add-user', { username: userName });
            if (response.data && !response.data.success) {
                setResponseMessage("משתמש כבר קיים");
                setIsSuccess(false);
                setIsResponsePopupOpen(true);
                return;
            }
            if (response.data && response.data.success === true) {
                setResponseMessage("משתמש נוסף בהצלחה");
                setIsSuccess(true);
                setIsResponsePopupOpen(true);
                getAllUsers()
            }
        } catch (error) {
            setResponseMessage('An unexpected error occurred');
            setIsSuccess(false);
            setIsResponsePopupOpen(true);
        } finally {
            setIsLoading(false);
        }
    };

    const handleRemoveUser = async (userId: number) => {

        setIsLoading(true)
        try {
            console.log(userId);
            const response = await axios.post('https://kaskas.pro/split-bill-server/remove-user', { id: userId });
            if (response.data && response.data.success === false) {
                setResponseMessage("משתמש לא נמצא");
                setIsSuccess(false);
                setIsResponsePopupOpen(true);
                return;
            }
            if (response.data && response.data.success === true) {
                setResponseMessage("משתמש הוסר בהצלחה");
                setIsSuccess(true);
                setIsResponsePopupOpen(true);
                getAllUsers()
            }
        } catch (error) {
            setResponseMessage('אירעה שגיאה');
            setIsSuccess(false);
            setIsResponsePopupOpen(true);
        } finally {
            setIsLoading(false);
        }
    };

    const getAdminUsers = async () => {
        try {
            const response = await axios.get('https://kaskas.pro/split-bill-server/admin/list-admins');
            setAdminUsers(response.data);
        } catch (error) {
            console.log(error);
        }
    };

    const handleCreateAdmin = async () => {
        if (!newAdminUsername.trim() || !newAdminPassword.trim()) {
            setResponseMessage('יש למלא שם משתמש וסיסמה');
            setIsSuccess(false);
            setIsResponsePopupOpen(true);
            return;
        }
        setIsAdminLoading(true);
        try {
            await axios.post('https://kaskas.pro/split-bill-server/admin/create-admin', {
                username: newAdminUsername.trim(),
                password: newAdminPassword.trim(),
            });
            setResponseMessage(`מנהל "${newAdminUsername.trim()}" נוצר בהצלחה`);
            setIsSuccess(true);
            setIsResponsePopupOpen(true);
            setNewAdminUsername('');
            setNewAdminPassword('');
            getAdminUsers();
        } catch (error: any) {
            const msg = error?.response?.data?.error === 'Username already exists'
                ? 'שם משתמש כבר קיים'
                : 'אירעה שגיאה ביצירת המנהל';
            setResponseMessage(msg);
            setIsSuccess(false);
            setIsResponsePopupOpen(true);
        } finally {
            setIsAdminLoading(false);
        }
    };

    const isUsernameValid = (): Boolean => {
        if (userName === '') {
            setResponseMessage('שם משתמש לא יכול להיות ריק!');
            setIsSuccess(false);
            setIsResponsePopupOpen(true);
            return false;
        }
        return true;
    };

    const handleCloseResponsePopup = () => {
        setIsResponsePopupOpen(false);
    };

    return (
        <div className="user-management">
            {isLoading && (
                <div className='loading-overlay'>
                    <div className='loading-spinner'></div>
                </div>
            )}
            <h1 className="user-management__title">ניהול משתמשים</h1>

            {isLoggedIn && (
                <div className="user-management__add-section">
                    <div className="input-group">
                        <input
                            type="text"
                            value={userName}
                            onChange={(e) => setUserName(e.target.value)}
                            placeholder="שם המשתמש"
                            className="input-field"
                        />
                        <button
                            className="add-button"
                            onClick={handleAddUser}
                            disabled={!userName.trim() || isLoading}
                        >
                            <FontAwesomeIcon icon={faUserPlus} className="button-icon" />
                            הוסף משתמש
                        </button>
                    </div>
                </div>
            )}

            <div className="user-management__list">
                <h2 className="user-management__subtitle">משתמשים קיימים</h2>
                {users.map(user => (
                    <div key={user.id} className="user-card">
                        <span className="user-card__name">
                            {user.name}
                        </span>
                        {isLoggedIn && (
                            <button
                                className="remove-button"
                                onClick={() => handleRemoveUser(user.id)}
                                disabled={isLoading}
                            >
                                <FontAwesomeIcon icon={faUserMinus} className="button-icon" />
                                הסר
                            </button>
                        )}
                    </div>
                ))}
                {users.length === 0 && (
                    <div className="empty-state">
                        אין משתמשים להצגה
                    </div>
                )}
            </div>

            {isLoading && (
                <div className="loading-overlay">
                    <div className="loading-spinner"></div>
                </div>
            )}

            {isLoggedIn && (
                <div className="admin-management">
                    <h2 className="user-management__subtitle">
                        <FontAwesomeIcon icon={faShieldAlt} className="button-icon" />
                        ניהול מנהלים
                    </h2>
                    <div className="admin-list">
                        {adminUsers.map(admin => (
                            <div key={admin.id} className="user-card admin-card">
                                <span className="user-card__name">{admin.username}</span>
                            </div>
                        ))}
                    </div>
                    <div className="input-group admin-input-group">
                        <input
                            type="text"
                            value={newAdminUsername}
                            onChange={(e) => setNewAdminUsername(e.target.value)}
                            placeholder="שם משתמש חדש"
                            className="input-field"
                        />
                        <input
                            type="password"
                            value={newAdminPassword}
                            onChange={(e) => setNewAdminPassword(e.target.value)}
                            placeholder="סיסמה"
                            className="input-field"
                        />
                        <button
                            className="add-button"
                            onClick={handleCreateAdmin}
                            disabled={!newAdminUsername.trim() || !newAdminPassword.trim() || isAdminLoading}
                        >
                            <FontAwesomeIcon icon={faUserPlus} className="button-icon" />
                            הוסף מנהל
                        </button>
                    </div>
                </div>
            )}

            <UserResponsePopup
                isOpen={isResponsePopupOpen}
                isSuccess={isSuccess}
                message={responseMessage}
                onClose={handleCloseResponsePopup}
            />
        </div>
    );
};

export default AddOrRemoveUser;